/**
 * Where a finding opens — one answer for every surface that links to one.
 *
 * A finding is a SYSTEM-scoped transaction group, so its page is the group page
 * read at that scope. Six call sites each spelled that out by hand, and one of
 * them (the alert-history badge) forgot the scope and opened the group under
 * the reader's own tenancy, where it is empty. The route lives here, once, as
 * TanStack `Link` parts rather than a `?situationScope=` string, so the router
 * owns the encoding.
 */

/**
 * The scope a finding is filed at when the wire names none: the system sweep's
 * (`commons.findings.identity`). A finding detected by a tenant's own run
 * carries its rung (`scope: "tenant" | "org" | "customer" | "user"`) and opens
 * at that scope — read at SYSTEM it would be an empty page.
 */
export const FINDING_SCOPE = "SYSTEM";

/** The dashboard's spelling of a finding's `scope` (`system` → `SYSTEM`). */
export function findingScope(scope?: string | null): string {
  const word = (scope ?? "").trim().toLowerCase();
  if (!word || word === "system" || word === "global") return FINDING_SCOPE;
  return word.toUpperCase();
}

export interface FindingRoute {
  to: "/groups/$id";
  params: { id: string };
  search: { situationScope: string };
}

/** `Link` / `navigate` parts for a finding's page. */
export function findingRoute(id: string, scope?: string | null): FindingRoute {
  return { to: "/groups/$id", params: { id }, search: { situationScope: findingScope(scope) } };
}

/** The same answer as a string, for a `<a href>` or a stored link. */
export function findingHref(id: string, scope?: string | null): string {
  return `/groups/${encodeURIComponent(id)}?situationScope=${findingScope(scope)}`;
}
